import type { SlidePosition } from "@hali-shares/deck-schema";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePresenter } from "../../deck/context";
import { slideTitle } from "../../deck/model";
import { THEMES } from "../../live/theme";
import { next, samePosition } from "../../nav/position";
import { ArrowIcon, DownloadIcon, ExitIcon, GridIcon, NotesIcon, ThemeIcon } from "./icons";

type Props = {
  pos: SlidePosition;
  idle: boolean;
  overviewOpen: boolean;
  onOverview: () => void;
  onPrev: () => void;
  onNext: () => void;
};

export function ControlBar({ pos, idle, overviewOpen, onOverview, onPrev, onNext }: Props) {
  const { model, goto, live } = usePresenter();
  const exitTitle =
    live && !live.ended ? "Back to your talks. The session keeps running; Present resumes it." : "Back to your talks";
  // At the last stop of the last slide, Next has nowhere to go: it becomes Exit.
  const atEnd = samePosition(next(model.slides, pos), pos);
  // Past a couple of dozen slides, dots won't fit: they become a thin track of equal segments.
  const dense = model.slides.length > 24;
  return (
    <div className="controls" data-idle={idle && !overviewOpen}>
      <div className="controls-left">
        <Link className="btn" to="/studio" title={exitTitle}>
          <ExitIcon />
          Exit
        </Link>
        <button type="button" className="btn" onClick={onOverview} aria-pressed={overviewOpen} aria-keyshortcuts="O">
          <GridIcon />
          {overviewOpen ? "Close" : "Overview"}
        </button>
        <SessionControl />
      </div>
      <div className="dots" data-dense={dense}>
        {model.slides.map((s, i) => (
          <button
            key={i}
            type="button"
            className="dot"
            aria-current={i === pos.slideIndex}
            aria-label={`Go to slide ${i + 1}: ${slideTitle(model, s)}`}
            onClick={() => goto({ slideIndex: i, stop: 0 })}
          />
        ))}
      </div>
      <div className="controls-right">
        <ThemeButton />
        <NotesButton />
        <span className="counter" data-testid="counter">
          {pos.slideIndex + 1} / {model.slides.length}
        </span>
        <button
          type="button"
          className="btn btn-icon"
          onClick={onPrev}
          aria-label="Previous"
          aria-keyshortcuts="ArrowLeft PageUp"
        >
          <ArrowIcon dir="left" />
        </button>
        {atEnd ? (
          <Link className="btn btn-next" to="/studio" title={exitTitle}>
            <ExitIcon />
            Exit
          </Link>
        ) : (
          <button
            type="button"
            className="btn btn-icon btn-next"
            onClick={onNext}
            aria-label="Next"
            aria-keyshortcuts="ArrowRight PageDown Space"
          >
            <ArrowIcon dir="right" />
          </button>
        )}
      </div>
    </div>
  );
}

/** End the live session (two clicks, so a stray click can't end a talk), or start a fresh one after it ended. */
function SessionControl() {
  const { live } = usePresenter();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!confirming) return;
    const t = window.setTimeout(() => setConfirming(false), 4000);
    return () => window.clearTimeout(t);
  }, [confirming]);

  if (!live)
    return (
      <span className="session-note" title="Rehearsal: no live session, polls are local">
        Rehearsal
      </span>
    );
  if (live.ended)
    return (
      <>
        <button type="button" className="btn" onClick={live.restart}>
          New session
        </button>
        <a className="btn" href={live.csvUrl} download title="Download every response as a CSV file">
          <DownloadIcon />
          CSV
        </a>
      </>
    );
  return confirming ? (
    <button
      type="button"
      className="btn btn-ink"
      onClick={() => {
        setConfirming(false);
        live.end();
      }}
    >
      Confirm: end session
    </button>
  ) : (
    <button type="button" className="btn" onClick={() => setConfirming(true)}>
      End session
    </button>
  );
}

/** Theme picker: a menu above the control bar, each theme with a preview. D cycles through the same list. */
function ThemeButton() {
  const { theme, setTheme } = usePresenter();
  const [open, setOpen] = useState(false);
  const current = THEMES.find((t) => t.id === theme)!;

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !(e.target as Element).closest(".theme-menu-wrap"))
        setOpen(false);
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <span className="theme-menu-wrap">
      <button
        type="button"
        className="btn btn-icon"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Theme: ${current.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-keyshortcuts="D"
      >
        <ThemeIcon dark={theme === "dark" || theme === "midnight"} />
      </button>
      {open && (
        <span className="theme-menu" role="menu" aria-label="Theme">
          {THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitemradio"
              aria-checked={t.id === theme}
              className="theme-item"
              onClick={() => {
                setTheme(t.id);
                setOpen(false);
              }}
            >
              <span className="theme-swatch" data-swatch={t.id} aria-hidden="true">
                <span className="theme-swatch-ink" />
                <span className="theme-swatch-accent" />
              </span>
              <span className="theme-text">
                <span className="theme-label">{t.label}</span>
                <span className="theme-note">{t.note}</span>
              </span>
            </button>
          ))}
          <span className="theme-hint">D cycles through them</span>
        </span>
      )}
    </span>
  );
}

function NotesButton() {
  const { openNotes } = usePresenter();
  return (
    <button type="button" className="btn" onClick={openNotes} aria-keyshortcuts="N">
      <NotesIcon />
      Notes
    </button>
  );
}
